import { getUserId } from "src/utils/auth";
import { request } from "."
import { Id } from "src/utils/types";

export type PsychologistFinancialValues = {
  numero_atendimentos: number,
  valor_psi: number,
  valor_total_atendimentos: number,
  montante_total_atendimentos: number,
  montante_total: number,
  montante_total_acoes: number,
}

export const getPsychologistMonthlyFinancialValues = async (): Promise<PsychologistFinancialValues> => {
  const id = getUserId();
  const response = await request.get<PsychologistFinancialValues>(`/psicologos/${id}/amount`);

  return response.data;
}

export type PsychologistServicesSummary = {
  qtde_atendimentos: number;
  montante_total_atendimentos: number;
  qtde_acoes: number;
  montante_total_acoes: number;
  nome_psicologo: string;
  colaboradores_atendidos: string[];
}

export const getPsychologistServicesSummary = async (startDate: string, endDate: string, id: Id): Promise<PsychologistServicesSummary> => {
  const response = await request.get<PsychologistServicesSummary>(`/psicologos/${id}/services-summary`, {
    params: {
      start_date: startDate,
      end_date: endDate,
    }
  })

  return response.data;
}

export type Psychologist = {
  id: number;
  name: string;
  email: string;
}

export const getPsychologists = async (): Promise<Psychologist[]> => {
  const response = await request.get<Psychologist[]>(`/psicologos`)

  if (!response.data) {
    return [];
  }

  return response.data;
}

export type CalendarView = "day" | "week" | "month";

export type CalendarEvent = {
  id?: number | string;
  schedule_id?: number | string;
  title?: string;
  start?: string;
  end?: string;
  scheduled_start_at?: string;
  scheduled_end_at?: string;
  date?: string;
  time?: string;
  status?: string;
  attendance_status?: string;
  service_id?: number | string | null;
  psychologist_id?: number | string;
  psychologist_name?: string;
  collaborator_id?: number | string;
  collaborator_name?: string;
  company_id?: number | string;
  company_name?: string;
  recurrence_group_id?: string;
  notes?: string;
  observacoes?: string;
  [key: string]: unknown;
};

export type PsychologistCalendarResponse = {
  view: CalendarView;
  total_events: number;
  events: CalendarEvent[];
};

export const getPsychologistCalendar = async (
  psychologistId: Id,
  startDate: string,
  endDate: string,
  view: CalendarView,
): Promise<PsychologistCalendarResponse> => {
  const response = await request.get<PsychologistCalendarResponse>(`/psicologos/${psychologistId}/calendar`, {
    params: {
      start_date: startDate,
      end_date: endDate,
      view,
    }
  });

  return response.data;
}

export type CalendarSchedulePayload = {
  company_id: number;
  collaborator_id: number;
  scheduled_start_at: string;
  duration_minutes: number;
  notes?: string;
  date?: string;
  time?: string;
};

export const getPsychologistCalendarSchedule = async (
  psychologistId: Id,
  scheduleId: Id
): Promise<CalendarEvent> => {
  const response = await request.get<CalendarEvent>(
    `/psicologos/${psychologistId}/calendar/${scheduleId}`
  );

  return response.data;
};

export const createPsychologistCalendarSchedule = async (
  psychologistId: Id,
  payload: CalendarSchedulePayload
): Promise<CalendarEvent> => {
  const response = await request.post<CalendarEvent>(
    `/psicologos/${psychologistId}/calendar`,
    payload
  );

  return response.data;
};

export const updatePsychologistCalendarSchedule = async (
  psychologistId: Id,
  scheduleId: Id,
  payload: CalendarSchedulePayload
): Promise<CalendarEvent> => {
  const response = await request.patch<CalendarEvent>(
    `/psicologos/${psychologistId}/calendar/${scheduleId}`,
    payload
  );

  return response.data;
};

export const deletePsychologistCalendarSchedule = async (
  psychologistId: Id,
  scheduleId: Id,
  reason?: string
): Promise<void> => {
  await request.delete(`/psicologos/${psychologistId}/calendar/${scheduleId}`, {
    data: reason ? { reason } : undefined,
  });
};

export type CalendarRecurrenceFrequency = "weekly" | "biweekly";

export type CalendarRecurrencePayload = {
  frequency: CalendarRecurrenceFrequency;
  months_ahead: number;
};

export type CalendarRecurrenceResponse = {
  source_schedule_id: number | string;
  recurrence_group_id: string;
  frequency: CalendarRecurrenceFrequency;
  months_ahead: number;
  until: string;
  created_count: number;
  skipped_count: number;
  events: CalendarEvent[];
};

export const createPsychologistCalendarRecurrence = async (
  psychologistId: Id,
  scheduleId: Id,
  payload: CalendarRecurrencePayload
): Promise<CalendarRecurrenceResponse> => {
  const response = await request.post<CalendarRecurrenceResponse>(
    `/psicologos/${psychologistId}/calendar/${scheduleId}/recurrence`,
    payload
  );

  return response.data;
};
